import TransComponent from 'components/TransComponent';
import { useMemo, memo } from 'react';
import styled from '@emotion/styled';
import { Preview } from '@knowledge/knowledges/general/edit/selectors/ItemSelect';
import { Button } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import StoreSelect, { useGetAdminStoresByStoreType } from '../edit/selectors/StoreSelect';
import { usePresetTimes } from '@knowledge/knowledges/general/edit/atom/presetTime';
import { T, TFnType, useTranslate } from '@tolgee/react';
import toLowerCase from 'utils/toLowerCase';
import { getStoreCount, Store } from 'components/StoreScopes';

type ConditionRendererProps = {
  values: Knowledge.KnowledgeItem[];
  stores?: Store[];
  onEdit: (knowledgeValue: Knowledge.KnowledgeItem) => void;
};

function ConditionRenderer(props: ConditionRendererProps) {
  const { values, stores, onEdit } = props;
  const { t } = useTranslate();

  return (
    <div>
      {values?.map((value, index) => {
        return (
          <div
            className="p-[12px] bg-[#f7f9fa] rounded-[2px] relative mb-[12px] last:mb-0 overflow-hidden"
            key={index}
          >
            <div className="absolute right-[8px] top-[8px]">
              <Button ghost type="text" icon={<FormOutlined />} onClick={() => onEdit(value)}>
                {t('phrase.编辑')}
              </Button>
            </div>
            <ConditionItemRender value={value} stores={stores} />
          </div>
        );
      })}
    </div>
  );
}

const class2 = 'text-right';
const class3 = 'ml-[4px] overflow-hidden';

export function ConditionItemRender(props: {
  value: Knowledge.Condition;
  stores?: Store[];
  allStores?: Store[];
}) {
  const { value, stores, allStores } = props;
  const { t } = useTranslate();

  return (
    <GridContainer>
      <div className={class2}>{t('marketing.店铺')}:</div>
      <div className={`${class3} flex-grow pr-[65px]`}>
        <StoreSelect
          editable={false}
          storeType={value?.storeScope?.storeType}
          value={value?.storeScope?.storeScope}
          dataSource={stores}
        />

        <StoreTag
          storeType={value?.storeScope?.storeType}
          value={value?.storeScope?.storeScope}
          dataSource={allStores || stores}
        />
      </div>

      <div className={class2}>{t('multiTimeSelect.时间')}:</div>
      <div className={class3}>
        <TimeRenderer value={value?.dtScope} />
      </div>

      <div className={class2}>{t('knowledge.商品')}:</div>
      <div className={class3}>
        <ItemRenderer value={value?.itemScope} />
      </div>
    </GridContainer>
  );
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: start;
  grid-row-gap: 12px;
  grid-column-gap: 4px;
`;

export function dayText(t: TFnType, value: Knowledge.Condition['dtScope']) {
  const { activeDuration, dayRepeat } = value;
  const repeatArr = dayRepeat?.[0]?.timeRanges;
  const repeatText = t('aide.每天timeRepeat循环生效', {
    timeRepeat: repeatArr?.map((each) => `${each.startAt} - ${each.endAt}`).join(' '),
  });
  if (activeDuration?.startAt && activeDuration?.endAt) {
    return t('aide.timeRange期间，timeRepeat', {
      timeRange: `${activeDuration?.startAt} - ${foreverText(t, activeDuration?.endAt)}`,
      timeRepeat: repeatText,
    });
  } else if (activeDuration?.startAt) {
    return t('aide.自startTime起，timeRepeat', {
      timeRange: activeDuration?.startAt,
      timeRepeat: repeatText,
    });
  } else {
    return repeatText;
  }
}

export function yearText(t: TFnType, value: Knowledge.Condition['dtScope']) {
  const { yearRepeat } = value;
  const repeatArr = yearRepeat?.[0]?.dtRanges;
  const repeatText = t('aide.每年timeRepeat循环生效', {
    timeRepeat: repeatArr?.map((each) => `${each.startAt} - ${each.endAt}`).join(' '),
  });
  return repeatText;
}

export function weekText(t: TFnType, value: Knowledge.Condition['dtScope']) {
  const { activeDuration, weekRepeat } = value;
  const weekLabel = {
    MONDAY: t('multiTimeSelect.周一'),
    TUESDAY: t('multiTimeSelect.周二'),
    WEDNESDAY: t('multiTimeSelect.周三'),
    THURSDAY: t('multiTimeSelect.周四'),
    FRIDAY: t('multiTimeSelect.周五'),
    SATURDAY: t('multiTimeSelect.周六'),
    SUNDAY: t('multiTimeSelect.周日'),
  };
  const repeatText = weekRepeat
    .map((each) => {
      const { dayOfWeeks, timeRanges } = each;
      return t('aide.每周的days timeRepeat', {
        days: dayOfWeeks.map((week) => weekLabel[week]).join(' '),
        timeRepeat: timeRanges
          .map((e) => {
            const { startAt, endAt } = e;
            return `[${startAt} ~ ${endAt}]`;
          })
          .join(' '),
      });
    })
    .join(' ');

  if (activeDuration?.startAt && activeDuration?.endAt) {
    return t('aide.timeRange期间，timeRepeat', {
      timeRange: `${activeDuration?.startAt} - ${foreverText(t, activeDuration?.endAt)}`,
      timeRepeat: repeatText,
    });
  } else if (activeDuration?.startAt) {
    return t('aide.自startTime起，timeRepeat', {
      timeRange: activeDuration?.startAt,
      timeRepeat: repeatText,
    });
  } else {
    return repeatText;
  }
}

function determineTimeRange(t, value: Knowledge.PresetTime) {
  if (!value) return '';
  if (value.dtScope.filterCode === 'DT_REPEAT') {
    if (value.dtScope.repeatType === 'DAY') {
      return dayText(t, value.dtScope);
    }
    if (value.dtScope.repeatType === 'YEAR') {
      return yearText(t, value.dtScope);
    }
    if (value.dtScope.repeatType === 'WEEK') {
      return weekText(t, value.dtScope);
    }
  }
  if (value.dtScope.filterCode === 'DT_SPECIFIED') {
    const { startAt, endAt } = value.dtScope.specifiedDtRange?.[0] || {};
    return `${startAt} - ${endAt}`;
  }
}

export function foreverText(t, value: string) {
  if (value.startsWith('2999')) {
    return t('multiTimeSelect.永久');
  }
  return value;
}

function TimeRenderer(props: { value: Knowledge.Condition['dtScope'] }) {
  const { value } = props;
  const { t } = useTranslate();
  const { data } = usePresetTimes();
  const ptmap = data?.reduce((acc: any, cur: any) => {
    acc[cur['id']] = cur;
    return acc;
  }, {});

  const tags = useMemo(() => {
    const res = [];
    if (value?.filterCode === 'DT_ALL') {
      res.push({
        type: 'text',
        value: t('knowledge.长期有效'),
      });
      return res;
    }

    if (value?.filterCode === 'DT_REFERENCE') {
      if (value?.referenceIds?.length > 0) {
        value?.referenceIds?.forEach((e) => {
          if (ptmap?.[e]) {
            res.push({
              type: 'popover',
              value: ptmap?.[e]?.name,
              title: determineTimeRange(t, ptmap?.[e]),
            });
          }
        });
      }
    }

    if (value?.filterCode === 'DT_SPECIFIED') {
      const { startAt, endAt } = value?.specifiedDtRange?.[0] || {};
      res.push({
        type: 'text',
        value: `${startAt} - ${foreverText(t, endAt)}`,
      });
      return res;
    }

    if (value?.filterCode === 'DT_REPEAT') {
      if (value?.repeatType === 'DAY') {
        res.push({
          type: 'text',
          value: dayText(t, value),
        });
      }
      if (value?.repeatType === 'WEEK') {
        res.push({
          type: 'text',
          value: weekText(t, value),
        });
      }
      if (value?.repeatType === 'YEAR') {
        res.push({
          type: 'text',
          value: yearText(t, value),
        });
      }
    }
    return res;
  }, [t, value, ptmap]);

  return tags.length > 0
    ? tags?.map((each, index) => {
        const { type, value, title } = each;
        return (
          <div className="mr-[6px] inline-flex" title={type === 'popover' ? title : ''} key={index}>
            [{value}]
          </div>
        );
      })
    : null;
}

function groupBy(arr = []) {
  const res = {};
  for (let i = 0; i < arr.length; i++) {
    const { region, externalType, storeType, id } = arr[i];
    if (!res[externalType]) {
      res[externalType] = 1;
    } else {
      res[externalType] += 1;
    }

    if (!res[storeType]) {
      res[storeType] = [id];
    } else {
      res[storeType].push(id);
    }

    if (!res[region]) {
      res[region] = 1;
    } else {
      res[region] += 1;
    }
  }

  return res;
}

function groupBy2(value?: General.StoreScope[]) {
  const res = {
    ids: [],
  };
  for (let i = 0; i < value?.length; i++) {
    const { platform, regionStoreScopes } = value[i];
    res[platform] = getStoreCount([value[i]]);
    for (let j = 0; j < regionStoreScopes.length; j++) {
      const { storeIds } = regionStoreScopes[j];
      res.ids = res.ids.concat(storeIds);
      // if (!res[region]) {
      //   res[region] = storeCount;
      // } else {
      //   res[region] += storeCount;
      // }
    }
  }
  return res;
}

function transKey(t: TFnType, key: General.PlatformCaseless) {
  const platform = [
    'SHOPEE',
    'LAZADA',
    'TOKOPEDIA',
    'TIKTOK',
    'TIKTOK_US',
    'ALIEXPRESS',
    'MERCADO',
    'KWAI',
  ];
  if (!platform.includes(key)) {
    return t(`region.${toLowerCase(key)}`);
  }
  return key;
}

function StoreTag(props: {
  value: Knowledge.Condition['storeScope'];
  storeType: Knowledge.StoreType;
  dataSource?: Store[];
}) {
  const { t } = useTranslate();
  const { value, dataSource } = props;
  const { data: stores } = useGetAdminStoresByStoreType('ALL', Boolean(dataSource));
  const data = dataSource || stores;
  const usedCount = value
    ?.map((e) => getStoreCount([e]))
    .reduce((acc, cur) => {
      return acc + cur;
    }, 0);

  const source = useMemo(() => {
    return groupBy(data);
  }, [data]);

  const valueSource = useMemo(() => {
    return groupBy2(value);
  }, [value]);

  const tags = useMemo(() => {
    const res = [];
    if (usedCount === data?.length) {
      res.push(`${t('knowledge.全部平台的全部店铺')}(${usedCount})`);
      res.push(`${t('knowledge.全部站点的全部店铺')}(${usedCount})`);
      res.push(
        `${t('knowledge.xx全部店铺', {
          x: `${t('店铺类型枚举.LOCAL')}&${t('店铺类型枚举.CROSS')}`,
        })}(${usedCount})`,
      );
    } else {
      for (let key in valueSource) {
        if (key === 'ids') continue;
        if (valueSource[key] === source[key]) {
          res.push(`${t('knowledge.xx全部店铺', { x: transKey(t, key) })}(${valueSource[key]})`);
        } else {
          res.push(`${t('knowledge.xx部分店铺', { x: transKey(t, key) })}(${valueSource[key]})`);
        }
      }

      const selectedIdsMap = valueSource.ids.reduce((acc, cur) => {
        acc[cur] = true;
        return acc;
      }, {});
      ['LOCAL', 'CROSS'].forEach((type) => {
        if (source[type]?.length > 0) {
          const a = source[type].filter((e) => selectedIdsMap[e]).length;
          if (a !== 0) {
            if (a === source[type].length) {
              res.push(`${t('knowledge.xx全部店铺', { x: t(`knowledge.${type}`) })}(${a})`);
            } else {
              res.push(`${t('knowledge.xx部分店铺', { x: t(`knowledge.${type}`) })}(${a})`);
            }
          }
        }
      });
    }
    return res;
  }, [t, usedCount, data, valueSource, source]);

  return tags?.length > 0
    ? tags.map((each, index) => {
        return (
          <div
            className="inline-flex ml-[8px] bg-[#f0f2f5] text-[515359] text-[12px] h-[18px] leading-[18px] px-[2px]"
            key={index}
          >
            {each}
          </div>
        );
      })
    : null;
}

function ItemRenderer(props: { value: Knowledge.Condition['itemScope'] }) {
  const { value } = props;
  const { t } = useTranslate();
  const tmap = {
    ONSALE: t('aide.现货'),
    PREORDER: t('aide.预售'),
  };
  const scope = value.saleTypes?.length === 2 ? '' : tmap[value.saleTypes?.[0]];
  return (
    <div className="overflow-hidden">
      {value.filterCode === 'ITEM_SPECIFIED' && value.specifiedItems?.length > 0 ? (
        <Preview ids={value.specifiedItems.map((e) => e.itemInternalId)} />
      ) : (
        <Tag>
          {t('knowledge.全部x商品', { x: scope })}
          {value?.exclude?.length > 0 ? (
            <span>
              (<ExcludeTag value={value?.exclude} />)
            </span>
          ) : (
            ''
          )}
        </Tag>
      )}
    </div>
  );
}

export const ExcludeTag = memo(
  (props: { value: Knowledge.Condition['itemScope']['exclude']; wrapTag: boolean }) => {
    const { value: exclude, wrapTag = false } = props;
    useTranslate();
    const children = (
      <T
        keyName="knowledge.除x"
        params={{
          y: (
            <TransComponent
              content={
                <span title={exclude?.map((e) => e.spuId)?.join(',')}>{`${exclude
                  ?.map((e) => e.spuId)
                  ?.join(',')}`}</span>
              }
            />
          ),
        }}
      />
    );

    return wrapTag ? <Tag>{children}</Tag> : children;
  },
);

const Tag = styled.div`
  height: 24px;
  border: 1px solid rgba(214, 232, 255, 1);
  background: #fff;
  padding: 2px 4px;
  line-height: 18px;
  font-size: 12px;
  color: #336fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content;
  max-width: 100%;
`;

export default ConditionRenderer;
